import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Grid,
  TextField,
  Button,
  IconButton,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  AvatarGroup,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Store as StoreIcon,
} from '@mui/icons-material';
import SectionTitle from '../components/SectionTitle';
import PageTitle from '../components/PageTitle';

const AdminResultsPage = () => {
  const navigate = useNavigate();
  const [results, setResults] = useState([]);
  const [newResult, setNewResult] = useState({
    store: '',
    round: '',
    player1: '',
    player2: '',
    winner: ''
  });
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Mock data
  const stores = [
    { id: 1, name: 'Tienda 1', logo: '🏪' },
    { id: 2, name: 'Tienda 2', logo: '🏬' },
  ];

  const players = [
    { id: 1, name: 'Juan Pérez', torCode: 'TOR001' },
    { id: 2, name: 'María García', torCode: 'TOR002' },
    { id: 3, name: 'Carlos López', torCode: 'TOR003' },
  ];

  // Mock initial results
  useEffect(() => {
    setResults([
      {
        id: 1,
        store: 'Tienda 1',
        round: 1,
        player1: 'Juan Pérez',
        player2: 'María García',
        winner: 'Juan Pérez',
        date: '2024-03-15'
      },
    ]);
  }, []);

  const handleBack = () => {
    navigate('/admin');
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!newResult.store || !newResult.round || !newResult.player1 || !newResult.player2 || !newResult.winner) {
      setError('Por favor completa todos los campos');
      return;
    }

    if (newResult.player1 === newResult.player2) {
      setError('Los jugadores deben ser diferentes');
      return;
    }

    // Mock adding a new result
    const newResultData = {
      id: results.length + 1,
      ...newResult,
      date: new Date().toISOString().split('T')[0]
    };

    setResults([newResultData, ...results]);
    setNewResult({
      store: newResult.store, // Keep the store selected
      round: newResult.round, // Keep the round
      player1: '',
      player2: '',
      winner: ''
    });
    setSuccess('Resultado agregado exitosamente');
    setError(null);

    setTimeout(() => setSuccess(null), 3000);
  };

  return (
    <Box sx={{ 
      p: { xs: 1, sm: 1 },
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      width: '90%',
      maxWidth: '1000px',
      mx: 'auto',
      bgcolor: 'transparent'
    }}>
      <Stack spacing={2} sx={{ width: '100%', bgcolor: 'transparent' }}>
        {/* Header with back button */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <IconButton 
            onClick={handleBack}
            sx={{ 
              bgcolor: 'action.hover',
              '&:hover': { bgcolor: 'primary.main', color: 'white' }
            }}
          >
            <ArrowBackIcon />
          </IconButton>
          <PageTitle title="Carga de Resultados" />
        </Box>

        {/* Form Section */}
        <SectionTitle title="Nuevo Resultado">
          <form onSubmit={handleSubmit}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Tienda</InputLabel>
                  <Select
                    value={newResult.store}
                    label="Tienda"
                    onChange={(e) => setNewResult({ ...newResult, store: e.target.value })}
                  >
                    {stores.map((store) => (
                      <MenuItem key={store.id} value={store.name}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography>{store.logo}</Typography>
                          {store.name}
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  fullWidth
                  label="Ronda"
                  type="number"
                  value={newResult.round}
                  onChange={(e) => setNewResult({ ...newResult, round: e.target.value })}
                  size="small"
                  inputProps={{ min: 1 }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Jugador 1</InputLabel>
                  <Select
                    value={newResult.player1}
                    label="Jugador 1"
                    onChange={(e) => setNewResult({ ...newResult, player1: e.target.value })}
                  >
                    {players.map((player) => (
                      <MenuItem key={player.id} value={player.name}>
                        {player.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Jugador 2</InputLabel>
                  <Select
                    value={newResult.player2}
                    label="Jugador 2"
                    onChange={(e) => setNewResult({ ...newResult, player2: e.target.value })}
                  >
                    {players.map((player) => (
                      <MenuItem key={player.id} value={player.name}>
                        {player.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Ganador</InputLabel>
                  <Select
                    value={newResult.winner}
                    label="Ganador"
                    onChange={(e) => setNewResult({ ...newResult, winner: e.target.value })}
                  >
                    {newResult.player1 && (
                      <MenuItem value={newResult.player1}>{newResult.player1}</MenuItem>
                    )}
                    {newResult.player2 && (
                      <MenuItem value={newResult.player2}>{newResult.player2}</MenuItem>
                    )}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={1}>
                <Button
                  fullWidth
                  variant="contained"
                  type="submit"
                  sx={{
                    bgcolor: '#c89e38',
                    '&:hover': { bgcolor: '#b38d2d' },
                    height: '40px'
                  }}
                >
                  <AddIcon />
                </Button>
              </Grid>
            </Grid>
          </form>

          {error && (
            <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}
          {success && (
            <Alert severity="success" sx={{ mt: 2 }} onClose={() => setSuccess(null)}>
              {success}
            </Alert>
          )}
        </SectionTitle>

        {/* Results List Section */}
        <SectionTitle 
          title="Resultados Recientes" 
          subtitle={`Total: ${results.length} partidas`}
        >
          <TableContainer component={Paper} sx={{ borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Tienda</TableCell>
                  <TableCell>Ronda</TableCell>
                  <TableCell>Jugadores</TableCell>
                  <TableCell>Ganador</TableCell>
                  <TableCell>Fecha</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {results.map((result) => (
                  <TableRow key={result.id}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <StoreIcon sx={{ color: 'text.secondary' }} />
                        {result.store}
                      </Box>
                    </TableCell>
                    <TableCell>{result.round}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <AvatarGroup max={2}>
                          <Avatar sx={{ width: 24, height: 24, bgcolor: '#c89e38' }}>
                            {result.player1[0]}
                          </Avatar>
                          <Avatar sx={{ width: 24, height: 24, bgcolor: '#c89e38' }}>
                            {result.player2[0]}
                          </Avatar>
                        </AvatarGroup>
                        <Typography variant="body2">
                          {result.player1} vs {result.player2}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          color: '#c89e38',
                          fontWeight: 'bold'
                        }}
                      >
                        {result.winner}
                      </Typography>
                    </TableCell>
                    <TableCell>{result.date}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </SectionTitle>
      </Stack>
    </Box>
  );
};

export default AdminResultsPage; 